import React from 'react'

import {sortBy} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {FormikErrors, useFormik} from 'formik'
import invariant from 'invariant'
import {useLocation, useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signIn, signOut} from '../../redux/features/auth'
import {setProvider} from '../../redux/features/core'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import * as authService from '../../../../commons/src/services/auth'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import {isAdminModePermission} from '../../libs/user'
import * as providerApi from '../../services/api/provider'
import * as userApi from '../../services/api/user'
import {FromLocationState, goToIndex} from '../../services/navigation'

import {ExtendedCognitoUserWithPermission, ProviderId} from '../../types'

import emailIcon from '../../../../commons/src/assets/icons/email.svg'
import lockIcon from '../../../../commons/src/assets/icons/lock.svg'
import signOutIcon from '../../../../commons/src/assets/icons/sign-out.svg'

import Button from '../../../../commons/src/components/button'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import PasswordInputField from '../../../../commons/src/components/password-input-field'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import SelectMenuItem from '../../../../commons/src/components/select-menu/SelectMenuItem'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'

import AuthLayoutPartial from './auth-layout-partial'

const SignInAdminPage = () => {
  const dispatch = useAppDispatch()
  const authUser = useAppSelector(state => state.auth.user)
  const authIsAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const provider = useAppSelector(state => state.core.provider)

  const [user, setUser] = React.useState<ExtendedCognitoUserWithPermission | null>(authUser || null)
  const isAdminMode = user ? isAdminModePermission(user.permission) : authIsAdminMode

  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as undefined | FromLocationState

  const [providerOptions, setProviderOptions] = React.useState<
    {value: ProviderId; label: string; appIds: string[]}[]
  >([])
  const [selectedProviderId, setSelectedProviderId] = React.useState<ProviderId>()

  const fetchProviderOptions = async () => {
    const providers = await providerApi.get()
    const sortedProviders = sortBy(providers, ['name'])
    const options = sortedProviders.map(p => ({
      value: p.id,
      label: p.name,
      appIds: p.apps.map(a => a.id),
    }))
    setProviderOptions(options)
  }

  React.useEffect(() => {
    if (user && !isAdminMode) {
      dispatch(signOut())
    }
    // Initially reset provider so that the page will show the provider selection
    if (provider) {
      dispatch(setProvider({provider: null}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    // Get provider selection options only when user is authenticated and in admin mode
    if (user && isAdminMode && !provider) {
      fetchProviderOptions()
    }
  }, [user, isAdminMode, provider])

  const getUserWithPermissions = async () => {
    const currentUser = await authService.getCurrentAuthUser()
    const userAttributes = await authService.getUserAttributes()
    const userId = userAttributes.sub
    const {permissions} = await userApi.getUserPermissions(userId as string)
    const newUser: ExtendedCognitoUserWithPermission = {
      ...currentUser,
      attributes: userAttributes,
      permission: permissions,
    }
    return newUser
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      // Additional errors
      notAuthorized: false,
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.combineValidators(validator.required, validator.email)(values.email)) {
        errors.email = 'true'
      }

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      return errors
    },
    onSubmit: (values, actions) => {
      return authService
        .authSignIn(values.email, values.password)
        .then(async signInOutput => {
          if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            actions.setFieldError('general', 'Something is wrong, please contact the support team.')
          } else {
            const userWithPermissions = await getUserWithPermissions()

            if (isAdminModePermission(userWithPermissions.permission)) {
              setUser(userWithPermissions)
              formik.resetForm()
            } else {
              actions.setFieldError('notAuthorized', 'true')
            }
          }
        })
        .catch(error => {
          if (error.name === 'NotAuthorizedException') {
            actions.setFieldError('notAuthorized', 'true')
          } else {
            actions.setFieldError('general', error.message)
          }
          dispatch(signOut())
        })
    },
  })

  const handleSignInAsProvider = async () => {
    invariant(user, 'User must be authenticated to access admin page')
    invariant(selectedProviderId, 'Missing provider ID')

    await dispatch(signIn({user, providerId: selectedProviderId}))

    if (locationState?.from) {
      navigate(locationState.from, {replace: true})
    } else {
      goToIndex(navigate, {replace: true})
    }
  }

  useKeyPress(
    preventDoubleClick(() => {
      formik.handleSubmit()
    }),
    'Enter'
  )

  if (user && isAdminMode) {
    return (
      <AuthLayoutPartial>
        <Stack space="400">
          <Headline label="Welcome Admin" className="u-align-center u-no-margin-bottom" />
          <Stack space="200">
            <Paragraph className="u-align-center u-no-margin-bottom">
              Please select a manufacturer:
            </Paragraph>
            <div>
              <SelectField
                placeholder="Manufacturer"
                name="adminProvider"
                size="200"
                disabled={providerOptions.length === 0}
                menu={
                  <SelectMenu values={providerOptions}>
                    {({label, appIds}) => (
                      <SelectMenuItem>
                        <span>
                          <strong>{label}</strong>{' '}
                          {appIds.length > 0 ? `(${appIds.join(', ')})` : ''}
                        </span>
                      </SelectMenuItem>
                    )}
                  </SelectMenu>
                }
                value={providerOptions.find(({value}) => value === selectedProviderId)}
                onChange={value => setSelectedProviderId(value.value)}
              />
            </div>
          </Stack>
          <SplitLayout
            verticalAlignment="start"
            left={
              <Button
                hierarchy="secondary"
                onClick={() => {
                  setUser(null)
                  dispatch(signOut())
                }}
                icon={<Icon source={signOutIcon} />}
              >
                Sign out
              </Button>
            }
            right={
              <Button onClick={handleSignInAsProvider} disabled={!selectedProviderId}>
                Proceed
              </Button>
            }
          />
        </Stack>
      </AuthLayoutPartial>
    )
  }

  return (
    <AuthLayoutPartial>
      <Stack space="300">
        <Headline label="Partner Portal Admin" className="u-align-center u-no-margin-bottom" />
        <form onSubmit={formik.handleSubmit}>
          {formik.errors.general && <Paragraph error>{formik.errors.general}</Paragraph>}
          {formik.errors.notAuthorized && (
            <Paragraph error>
              We can&apos;t find an account matching your sign in information or the provided
              password is wrong.
            </Paragraph>
          )}
          <FormRow>
            <InputField
              name="email"
              type="email"
              placeholder="E-Mail"
              icon={<Icon source={emailIcon} />}
              size="200"
              autoFocus
              appearance={formik.submitCount > 0 && !!formik.errors.email ? 'error' : undefined}
              onChange={(v, n, event) => formik.handleChange(event)}
              value={formik.values.email}
              onBlur={formik.handleBlur}
            />
          </FormRow>
          <PasswordInputField
            placeholder="Password"
            name="password"
            autoComplete="new-password"
            icon={<Icon source={lockIcon} />}
            size="200"
            appearance={formik.submitCount > 0 && !!formik.errors.password ? 'error' : undefined}
            onChange={(v, n, event) => formik.handleChange(event)}
            value={formik.values.password}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
          />
        </form>
        <Button
          as="a"
          block
          disabled={formik.isSubmitting || (!isEmpty(formik.errors) && formik.submitCount > 0)}
          onClick={preventDoubleClick(() => formik.handleSubmit())}
        >
          Continue
        </Button>
      </Stack>
    </AuthLayoutPartial>
  )
}

export default SignInAdminPage
